import { arePairHalves, isPairSeatType, pairKindOf, pairSideOf } from '@cineamo/api/models/seat/seat-pairs';
import { SeatType } from '@cineamo/api/models/seat/SeatDto.types';

import { withOpacity, getRelativeLuminance, hexToRgb } from '@cineamo/ui/src/helpers/color-helper';

import { Point2D, deg2Rad, rectContainsPoint, rotatePoint } from '../helpers/math';

import { CameraState } from './Camera';
import { SeatMapData } from './SeatMap';
import { Position, RoomObject, Seat, SeatCategory } from './typedefs';

export type HEX_COLOR = `#${string}`;

export interface Transform {
    scale: number;
    offset: Point2D;
}

export interface DrawerOptions {
    seatCornerRadius: number;
    tableCornerRadius: number;
    seatColorsByCategory: { [key: SeatCategory['name']]: HEX_COLOR };
    selectedSeatColor?: HEX_COLOR;
    selectedSeatCenterColor: HEX_COLOR;
    screenLabel: string;
    icons?: Record<string, HTMLImageElement>;
}

export const defaultOptions: DrawerOptions = {
    seatCornerRadius: 4,
    tableCornerRadius: 4,
    seatColorsByCategory: {
        standard: '#00FFDC',
        premium: '#FFA620',
        love: '#FFDE2E',
        VIP: '#6BAFFF'
    },
    selectedSeatCenterColor: '#000023',
    screenLabel: 'Canvas'
};

const OVERHEAD_OBJECT_TYPES: ReadonlySet<string> = new Set(['speaker', 'projector']);
const FIXTURE_FILL = 'rgba(128, 128, 145, 0.28)';
const FIXTURE_STROKE = 'rgba(128, 128, 145, 0.6)';

export class Drawer {
    private data?: SeatMapData;
    private options: DrawerOptions;

    get seatCornerRadius() {
        return this.options.seatCornerRadius;
    }

    constructor(
        private ctx: CanvasRenderingContext2D,
        options: Partial<DrawerOptions> = {}
    ) {
        this.options = {
            ...defaultOptions,
            ...options
        };
    }

    /**
     * Draw a complete frame with camera transform applied.
     * This is the primary render method used by the spring-animated SeatMap.
     */
    drawFrame(data: SeatMapData, camera: CameraState) {
        this.data = data;

        const canvasWidth = this.ctx.canvas.width;
        const canvasHeight = this.ctx.canvas.height;
        if (!canvasWidth || !canvasHeight) return;

        // Reset transform and clear
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        // Apply camera transform
        this.ctx.setTransform(camera.scale, 0, 0, camera.scale, camera.x, camera.y);

        // Draw scene in world space
        const canvasHalfWidth = canvasWidth / 2;
        const roomScale = canvasWidth / data.room.width;
        const screenDepth = this.calculateScreenDepth(data.roomObjects ?? [], canvasHeight, roomScale);

        this.drawScreen(canvasWidth);

        // Room objects (tables) behind seats
        if (data.roomObjects) {
            for (const roomObject of data.roomObjects.filter(this.NotScreen)) {
                this.drawRoomObject(roomObject, roomScale, canvasHalfWidth, screenDepth);
            }
        }

        // Loveseat bridges behind individual seats
        this.drawPairBridges(data.seats, roomScale, canvasHalfWidth, screenDepth);

        // Individual seats
        for (const seat of data.seats) {
            this.drawSeat(seat, roomScale, canvasHalfWidth, screenDepth);
        }
    }

    /**
     * Backward-compatible draw without camera (uses identity transform).
     */
    draw(data: SeatMapData, options: Partial<DrawerOptions> = {}) {
        this.data = data;
        this.options = { ...this.options, ...options };
        this.drawFrame(data, { x: 0, y: 0, scale: 1 });
    }

    // Filter function to exclude screens from other room objects
    private NotScreen = (roomObjectA: RoomObject) => roomObjectA.type !== 'screen';

    /**
     * Backward-compatible redraw.
     */
    redraw(options: Partial<DrawerOptions> = {}) {
        this.options = { ...this.options, ...options };
        if (this.data) this.draw(this.data);
    }

    findSeatByPoint(point: Point2D): Seat | null {
        if (!this.data) return null;

        const canvasWidth = this.ctx.canvas.width;
        const canvasHeight = this.ctx.canvas.height;
        const canvasHalfWidth = this.ctx.canvas.width / 2;
        const roomScale = canvasWidth / this.data.room.width;
        const screenDepth = this.calculateScreenDepth(this.data.roomObjects ?? [], canvasHeight, roomScale);
        for (const seat of this.data.seats) {
            const seatPosition = this.translateSeatPosition(seat, roomScale, canvasHalfWidth, screenDepth);
            const width = seat.width * roomScale,
                depth = seat.depth * roomScale,
                halfWidth = width / 2,
                halfDepth = depth / 2;

            const rotatedPoint = rotatePoint(point, seatPosition, seat.rotation.z);
            if (
                rectContainsPoint(
                    {
                        top: seatPosition.y - halfDepth,
                        right: seatPosition.x + halfWidth,
                        bottom: seatPosition.y + halfDepth,
                        left: seatPosition.x - halfWidth
                    },
                    rotatedPoint
                )
            ) {
                return seat;
            }
        }

        return null;
    }

    /**
     * Draws a "screen" at the top of the room
     */
    private drawScreen(canvasWidth: number) {
        const screenObject = this.getScreenObject(this.data?.roomObjects || []);
        const roomScale = canvasWidth / (this.data?.room.width || 1);
        const canvasHalfWidth = canvasWidth / 2;

        // label
        const fontSize = 28;
        this.ctx.font = `700 ${fontSize}px Montserrat`;
        this.ctx.fillStyle = '#808091';
        this.ctx.textAlign = 'center';
        this.ctx.fillText(this.options.screenLabel, canvasWidth / 2, fontSize);

        // screen line
        this.ctx.lineWidth = 4;
        this.ctx.beginPath();

        let startX: number;
        let endX: number;
        let screenY: number;

        if (screenObject) {
            // Use screen object position and dimensions
            const transform = screenObject.dto._embedded?.transform;
            const screenWidth = screenObject.width * roomScale;
            const halfScreenWidth = screenWidth / 2;

            const screenCenterX = (transform?.positionX ?? 0) * roomScale + canvasHalfWidth;
            startX = screenCenterX - halfScreenWidth;
            endX = screenCenterX + halfScreenWidth;

            // Position screen based on its transform data
            const fakePosition = {
                y: transform?.positionY ?? -screenObject.height
            };
            screenY = -fakePosition.y * roomScale + (fontSize + 2);
        } else {
            // Fallback: use current logic but with straight line
            const verticalOffset = fontSize + 2;
            const horizontalPadding = canvasWidth * 0.02;

            startX = horizontalPadding;
            endX = canvasWidth - horizontalPadding;
            screenY = verticalOffset + 10;
        }

        if (screenObject) {
            const transform = screenObject.dto._embedded?.transform;
            const screenWidth = screenObject.width * roomScale;
            const screenDepth = screenObject.depth * roomScale;
            const halfScreenWidth = screenWidth / 2;
            const halfScreenDepth = screenDepth / 2;

            const screenCenterX = (transform?.positionX ?? 0) * roomScale + canvasHalfWidth;
            const fakePosition = {
                y: transform?.positionY ?? -screenObject.height
            };
            const screenCenterY = -fakePosition.y * roomScale + (fontSize + 2);

            this.ctx.fillStyle = 'rgba(179,179,189,0.1)';
            this.ctx.fillRect(
                screenCenterX - halfScreenWidth,
                screenCenterY - halfScreenDepth,
                screenWidth,
                screenDepth
            );

            this.ctx.moveTo(screenCenterX - halfScreenWidth, screenCenterY + halfScreenDepth);
            this.ctx.lineTo(screenCenterX + halfScreenWidth, screenCenterY + halfScreenDepth);
        } else {
            const verticalOffset = fontSize + 2;
            const screenRectHeight = verticalOffset + 10;

            this.ctx.fillStyle = 'rgba(179,179,189,0.1)';
            this.ctx.fillRect(startX, 0, endX - startX, screenRectHeight);

            this.ctx.moveTo(startX, screenY);
            this.ctx.lineTo(endX, screenY);
        }

        const gradient = this.ctx.createLinearGradient(0, 0, canvasWidth, 0);
        gradient.addColorStop(0, 'rgba(219,219,226,1)');
        gradient.addColorStop(0.3, 'rgba(179,179,189,1)');
        gradient.addColorStop(0.7, 'rgba(179,179,189,1)');
        gradient.addColorStop(1, 'rgba(219,219,226,1)');

        this.ctx.strokeStyle = gradient;
        this.ctx.stroke();
    }

    private getScreenObject(roomObjects: RoomObject[]): RoomObject | undefined {
        return roomObjects.find((it) => it.type === 'screen');
    }

    private calculateScreenDepth(roomObjects: RoomObject[], canvasHeight: number, roomScale: number): number {
        const screenObject = this.getScreenObject(roomObjects);
        if (screenObject) {
            return screenObject.depth * roomScale;
        }

        const fontSize = 28;
        const verticalOffset = fontSize + 2;
        const verticalPadding = canvasHeight * 0.06 + verticalOffset;
        return verticalPadding;
    }

    /**
     * Draw connecting bridges between the two halves of a pair (loveseat, bed)
     */
    private drawPairBridges(seats: Seat[], roomScale: number, canvasHalfWidth: number, screenDepth: number) {
        // Group seats by row
        const rowGroups = new Map<string, Seat[]>();
        for (const seat of seats) {
            if (!isPairSeatType(seat.type)) continue;
            const row = seat.rowName || 'unknown';
            if (!rowGroups.has(row)) rowGroups.set(row, []);
            rowGroups.get(row)!.push(seat);
        }

        for (const rowSeats of rowGroups.values()) {
            const sorted = [...rowSeats].sort((a, b) => a.position.x - b.position.x);
            const usedIds = new Set<number>();

            for (const left of sorted) {
                if (pairSideOf(left.type) !== 'left' || usedIds.has(left.id)) continue;

                const leftPos = this.translateSeatPosition(left, roomScale, canvasHalfWidth, screenDepth);
                const leftHalfW = (left.width * roomScale) / 2;
                const leftRightEdge = leftPos.x + leftHalfW;

                // Find the nearest right half of the same kind to the right
                for (const right of sorted) {
                    if (!arePairHalves(left.type, right.type) || usedIds.has(right.id)) continue;
                    const rightPos = this.translateSeatPosition(right, roomScale, canvasHalfWidth, screenDepth);
                    const rightHalfW = (right.width * roomScale) / 2;
                    const rightLeftEdge = rightPos.x - rightHalfW;

                    if (rightLeftEdge < leftRightEdge) continue;

                    const bridgeWidth = rightLeftEdge - leftRightEdge;
                    if (bridgeWidth <= 0 || bridgeWidth > left.width * roomScale * 2) continue;

                    usedIds.add(left.id);
                    usedIds.add(right.id);

                    // Use same color as the pair
                    const color = this.getSeatColor(left);
                    const bridgeDepth = Math.max(left.depth, right.depth) * roomScale;
                    const bridgeY = Math.min(leftPos.y, rightPos.y) - bridgeDepth / 2;

                    this.ctx.beginPath();
                    this.ctx.rect(leftRightEdge, bridgeY, bridgeWidth, bridgeDepth);
                    this.ctx.fillStyle = color;
                    this.ctx.fill();
                    break;
                }
            }
        }
    }

    private drawSeat(seat: Seat, roomScale: number, canvasHalfWidth: number, screenDepth?: number) {
        const position = this.translateSeatPosition(seat, roomScale, canvasHalfWidth, screenDepth);
        const rotation = deg2Rad(seat.rotation.z);
        const width = seat.width * roomScale,
            depth = seat.depth * roomScale,
            halfWidth = width / 2,
            halfDepth = depth / 2;

        this.ctx.translate(position.x, position.y);
        this.ctx.rotate(rotation);

        this.ctx.beginPath();
        // A beanbag is a round cushion, everything else a rounded rectangle.
        const cornerRadius = seat.type === SeatType.BEANBAG ? Math.min(halfWidth, halfDepth) : this.seatCornerRadius;
        this.ctx.roundRect(-halfWidth, -halfDepth, width, depth, cornerRadius);
        const seatColor = this.getSeatColor(seat);
        this.ctx.fillStyle = seatColor;
        this.ctx.fill();
        const isSeatColorLight = getRelativeLuminance(hexToRgb(seatColor)) > 0.5;

        const innerIconHalfSize = (Math.min(halfWidth, halfDepth) / 3) * 2;

        let seatIcon = null;
        if (seat.isSelected) {
            seatIcon = this.options.icons?.['dot'];
        } else if (seat.isLocked) {
            seatIcon = this.options.icons?.['close'];
        } else if ([SeatType.WHEELCHAIR, SeatType.DISABLED].includes(seat.type)) {
            seatIcon = isSeatColorLight
                ? this.options.icons?.['wheelchair_dark']
                : this.options.icons?.['wheelchair_light'];
        } else if (pairKindOf(seat.type) === 'loveseat') {
            seatIcon = isSeatColorLight ? this.options.icons?.['heart_dark'] : this.options.icons?.['heart_light'];
        } else if (pairKindOf(seat.type) === 'bed') {
            seatIcon = isSeatColorLight ? this.options.icons?.['bed_dark'] : this.options.icons?.['bed_light'];
        } else if (seat.type === SeatType.BEANBAG) {
            seatIcon = isSeatColorLight ? this.options.icons?.['beanbag_dark'] : this.options.icons?.['beanbag_light'];
        }

        if (seatIcon) {
            this.ctx.drawImage(
                seatIcon,
                -innerIconHalfSize,
                -innerIconHalfSize,
                innerIconHalfSize * 2,
                innerIconHalfSize * 2
            );
        }

        this.ctx.rotate(-rotation);
        this.ctx.translate(-position.x, -position.y);
    }

    private translateSeatPosition(
        seat: Seat,
        roomScale: number,
        canvasHalfWidth: number,
        screenDepth?: number
    ): Position {
        return {
            x: seat.position.x * roomScale + canvasHalfWidth,
            y: -seat.position.y * roomScale + (screenDepth ?? 0),
            z: seat.position.z
        };
    }

    private drawRoomObject(roomObject: RoomObject, roomScale: number, canvasHalfWidth: number, screenDepth?: number) {
        // Ceiling-mounted equipment says nothing about where guests can sit.
        if (roomObject.type && OVERHEAD_OBJECT_TYPES.has(roomObject.type)) return;

        const position = this.translateRoomObjectPosition(roomObject, roomScale, canvasHalfWidth, screenDepth);
        const width = roomObject.width * roomScale;
        const depth = roomObject.depth * roomScale;
        const halfWidth = width / 2;
        const halfDepth = depth / 2;
        // Tables have always been drawn as plain white blocks; the room's fixtures (stage, pillars,
        // walls, stairs, doors) are muted so they read as the building rather than as something bookable.
        const isTable = roomObject.type === 'table';
        const rotation = isTable ? 0 : deg2Rad(roomObject.dto._embedded?.transform?.rotationZ ?? 0);

        this.ctx.translate(position.x, position.y);
        this.ctx.rotate(rotation);

        this.ctx.beginPath();
        if (roomObject.type === 'pillar' && Math.abs(width - depth) < Math.max(width, depth) * 0.15) {
            this.ctx.ellipse(0, 0, halfWidth, halfDepth, 0, 0, Math.PI * 2);
        } else {
            this.ctx.roundRect(-halfWidth, -halfDepth, width, depth, this.seatCornerRadius);
        }
        this.ctx.fillStyle = isTable ? '#FFFFFF' : FIXTURE_FILL;
        this.ctx.fill();

        this.ctx.strokeStyle = isTable ? '#E5E5E5' : FIXTURE_STROKE;
        this.ctx.lineWidth = 1;
        this.ctx.stroke();

        this.ctx.rotate(-rotation);
        this.ctx.translate(-position.x, -position.y);
    }

    private translateRoomObjectPosition(
        roomObject: RoomObject,
        roomScale: number,
        canvasHalfWidth: number,
        screenDepth?: number
    ): Position {
        const transform = roomObject.dto._embedded?.transform;
        const fakePosition = {
            x: transform?.positionX ?? 0,
            y: transform?.positionY ?? -roomObject.height,
            z: transform?.positionZ ?? 0
        };

        return {
            x: fakePosition.x * roomScale + canvasHalfWidth,
            y: -fakePosition.y * roomScale + (screenDepth ?? 0) + 8,
            z: fakePosition.z
        };
    }

    private getSeatColor(seat: Seat): HEX_COLOR {
        let color =
            seat.isSelected && this.options.selectedSeatColor
                ? this.options.selectedSeatColor
                : (this.options.seatColorsByCategory?.[seat.category?.name ?? ''] ?? '#00FFDC');

        if (seat.isLocked) {
            color = withOpacity(color, 0.5) ?? color;
        }

        if (seat.isError) {
            color = '#FF3933';
        }
        return color;
    }
}
